import React, { useCallback, useMemo, useState } from "react";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { WorkspaceAvatar } from "@design-system/Avatar/WorkspaceAvatar";
import {
    WorkspaceAvatarGroup,
    type WorkspaceAvatarGroupItem,
} from "@components/avatar/WorkspaceAvatarGroup";
import { FilterActionsFooter } from "@design-system/Filter/FilterActionsFooter/FilterActionsFooter";
import { useOrganizationClients } from "../useOrganizationClients";

interface OrganizationClientFilterComboboxProps {
    clientWorkspaceIds?: string[];
    onChange: (clientWorkspaceIds: string[] | undefined) => void;
}

const matchesPrefilteredQuery = () => true;

/**
 * The firm Activity Clients picker. Its button shows only an avatar stack, the
 * picked clients' or every client's while none is picked, and is named
 * "All clients", the picked client or "N clients".
 */
export const OrganizationClientFilterCombobox: React.FC<
    OrganizationClientFilterComboboxProps
> = ({ clientWorkspaceIds, onChange }) => {
    const [search, setSearch] = useState("");
    const { data: workspaces = [] } = useOrganizationClients();

    const lowerSearch = search.toLowerCase();

    const workspacesById = useMemo(
        () => new Map(workspaces.map((workspace) => [workspace.id, workspace])),
        [workspaces],
    );

    const filteredItems = useMemo<Array<PickerItemData<string>>>(() => {
        const matching = search
            ? workspaces.filter((workspace) =>
                  workspace.name.toLowerCase().includes(lowerSearch),
              )
            : workspaces;

        return matching.map((workspace) => ({
            value: workspace.id,
            label: workspace.name,
            leading: (
                <WorkspaceAvatar workspace={workspace} size="xs" aria-hidden />
            ),
        }));
    }, [workspaces, search, lowerSearch]);

    const selectedWorkspaces = useMemo(
        () =>
            (clientWorkspaceIds ?? []).flatMap((id) => {
                const workspace = workspacesById.get(id);
                return workspace ? [workspace] : [];
            }),
        [clientWorkspaceIds, workspacesById],
    );

    const selectedCount = selectedWorkspaces.length;

    const avatarGroupItems = useMemo<WorkspaceAvatarGroupItem[]>(
        () =>
            (selectedCount > 0 ? selectedWorkspaces : workspaces).map(
                (workspace) => ({ workspace }),
            ),
        [selectedWorkspaces, selectedCount, workspaces],
    );

    const triggerLabel = useMemo(() => {
        if (selectedCount === 0) {
            return "All clients";
        }
        if (selectedCount === 1) {
            return selectedWorkspaces[0].name;
        }
        return `${selectedCount} clients`;
    }, [selectedWorkspaces, selectedCount]);

    const triggerValue =
        avatarGroupItems.length > 0 ? (
            <WorkspaceAvatarGroup
                workspaces={avatarGroupItems}
                max={3}
                size="xs"
            />
        ) : (
            triggerLabel
        );

    const resetFilter = useCallback(() => {
        onChange(undefined);
    }, [onChange]);

    return (
        <Picker.Select
            items={filteredItems}
            multiple
            searchable
            searchPlaceholder="Search…"
            filter={matchesPrefilteredQuery}
            value={selectedWorkspaces.map((workspace) => workspace.id)}
            onOpenChange={(open) => {
                if (!open) {
                    setSearch("");
                }
            }}
            onInputValueChange={setSearch}
            onValueChange={(ids) => onChange(ids.length > 0 ? ids : undefined)}
            trigger={
                <Picker.Button
                    variant="primary"
                    size="md"
                    aria-label={triggerLabel}
                >
                    {triggerValue}
                </Picker.Button>
            }
            chevron="none"
        >
            {selectedCount > 0 && (
                <Picker.Footer>
                    <FilterActionsFooter
                        deselectFunction={resetFilter}
                        selectedCount={selectedCount}
                        hideBorderAndPadding
                    />
                </Picker.Footer>
            )}
        </Picker.Select>
    );
};
